import { UserTaskTypeConstants } from "finhelper";
import React, { useMemo, useState, useCallback, type FC, type SVGProps } from "react";

import { ReactComponent as UsersIcon } from "#app/assets/icons/users.svg?react";
import { ReactComponent as CaseIcon } from "#app/assets/task/case.svg?react";
import { ReactComponent as LearnIcon } from "#app/assets/task/learn.svg?react";
import { ReactComponent as LetterIcon } from "#app/assets/task/letter.svg?react";
import { ReactComponent as PhoneIcon } from "#app/assets/task/phone.svg?react";
import { ReactComponent as UserIcon } from "#app/assets/task/user.svg?react";

interface TaskTypeInterface {
    id: string | number;
    icon: React.ReactNode;
    text: string;
}

type TaskTypeIconComponent = FC<SVGProps<SVGSVGElement>>;

type TaskTypeConfig = {
    id: string | number;
    Icon: TaskTypeIconComponent;
    text: string;
};

const taskTypeConfigs: TaskTypeConfig[] = [
    {
        id: UserTaskTypeConstants.CALLS,
        Icon: PhoneIcon,
        text: UserTaskTypeConstants.getTextByType(UserTaskTypeConstants.CALLS),
    },
    {
        id: UserTaskTypeConstants.MEETINGS,
        Icon: UserIcon,
        text: UserTaskTypeConstants.getTextByType(UserTaskTypeConstants.MEETINGS),
    },
    {
        id: UserTaskTypeConstants.CONSULTATION,
        Icon: LearnIcon,
        text: UserTaskTypeConstants.getTextByType(UserTaskTypeConstants.CONSULTATION),
    },
    {
        id: UserTaskTypeConstants.LETTERS,
        Icon: LetterIcon,
        text: UserTaskTypeConstants.getTextByType(UserTaskTypeConstants.LETTERS),
    },
    {
        id: UserTaskTypeConstants.OTHER,
        Icon: CaseIcon,
        text: UserTaskTypeConstants.getTextByType(UserTaskTypeConstants.OTHER),
    },
    {
        id: UserTaskTypeConstants.PERSONAL,
        Icon: UsersIcon,
        text: UserTaskTypeConstants.getTextByType(UserTaskTypeConstants.PERSONAL),
    },
];

const buildIconsList = (): TaskTypeInterface[] =>
    taskTypeConfigs.map(({ id, Icon, text }) => ({
        id,
        icon: React.createElement(Icon),
        text,
    }));

/**
 * Хук для выбора иконок
 * @param typeId - ID текущей иконки, берется из UserTaskTypeConstants
 * @returns три мемоизованных хука:
 * iconsList - возвращает список загруженных в хук иконок,
 * currentIcon - возвращает текущую иконку,
 * setIcon - меняет возвращаемую по запросам currentIcon.
 */
export const useTaskTypesIcons = (
    typeId: number | null | undefined = null
): {
    setIcon: (id: (string | number | null | undefined)) => void,
    currentIcon: TaskTypeInterface,
    iconsList: Array<TaskTypeInterface>
} => {
    const iconsListMemo = useMemo<Array<TaskTypeInterface>>(
        () => buildIconsList(),
        []
    );

    const [current, setCurrent] = useState<TaskTypeInterface>(() => {
        const initialIcon = iconsListMemo.find((icon) => icon.id === typeId);
        return initialIcon || iconsListMemo[0];
    });

    const setIcon = useCallback(
        (id: string | number | null | undefined): void => {
            if (!id) throw new Error("Параметр id иконки должен быть задан явно");
            if (id === current.id) return;

            const foundIcon = iconsListMemo.find((icon) => icon.id === id);
            if (foundIcon) setCurrent(foundIcon);
            else throw new Error(`Иконка с id ${id} не найдена`);
        },
        [current.id, iconsListMemo]
    );

    return useMemo(
        () => ({
            iconsList: iconsListMemo,
            currentIcon: current,
            setIcon,
        }),
        [iconsListMemo, current, setIcon]
    );
};
